/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import cx from 'classnames';

import { Button } from '@coursera/cds-core';

import type { Props as CoachTriggerButtonProps } from 'bundles/ai-coach-platform/components/building-blocks/CoachTriggerButton';
import SparkleIcon from 'bundles/unified-common/components/coach-on-discovery/SparkleIcon';

export type Props = CoachTriggerButtonProps & {
  /** If true, animation will play automatically when component mounts */
  showAnimationOnMount?: boolean;
};

const styles = {
  coachButton: ({ showBorder = true }: { showBorder?: boolean }) => css`
    /* Custom gradients */
    --default-gradient:
      radial-gradient(436.45% 146.99% at 19.79% -48.61%, #1cffba 0%, rgb(8 255 181 / 0%) 65.49%),
      linear-gradient(275deg, #0ff0ff -26.6%, #4b95ff 31.52%, #1e72eb 110.8%);

    border-radius: var(--cds-border-radius-max);
    box-shadow: none;

    &:hover,
    &:active,
    &.selected,
    &.cds-button-focusVisible {
      box-shadow: none;
    }

    // override the focus outline on the button.
    &.cds-button-focusVisible.cds-button-disableElevation::after {
      border-radius: var(--cds-border-radius-max);
      box-shadow:
        0 0 0 1px var(--cds-color-emphasis-primary-stroke-default),
        0 0 0 2px var(--cds-color-interactive-background-primary-hover-weak);
    }

    &.coach-secondary-button {
      position: relative;
      z-index: 1;

      /** button border color */
      background: ${showBorder ? 'var(--cds-color-neutral-stroke-primary-weak)' : 'transparent'};

      .cds-button-label {
        color: var(--cds-color-neutral-primary);
      }

      &::before {
        content: '';
        position: absolute;
        left: 1px;
        right: 1px;
        top: 1px;
        bottom: 1px;
        border-radius: var(--cds-border-radius-max);
        z-index: -1;

        /** button background color */
        background: var(--cds-color-neutral-background-primary);
      }

      &:hover,
      &:active,
      &.cds-button-focusVisible,
      &.selected {
        color: var(--cds-color-neutral-primary);
        text-decoration: underline;
      }

      &:active {
        &::before {
          border-radius: var(--cds-border-radius-max);
        }

        background: var(--default-gradient);
      }

      &.selected {
        background: var(--default-gradient);

        &::before {
          left: 2px;
          right: 2px;
          top: 2px;
          bottom: 2px;
        }
      }

      &[aria-disabled='true'] {
        .animation-wrapper {
          &::after {
            background: unset;
          }
        }

        .cds-button-label {
          color: var(--cds-color-neutral-disabled-strong);
        }

        svg path {
          fill: var(--cds-color-neutral-disabled-strong);
        }
      }
    }
  `,
  animated: css`
    --coursera-ai-color-teal: #0ff0ff;
    @property --angle {
      syntax: '<angle>';
      inherits: false;
      initial-value: 0deg;
    }

    @keyframes spin {
      from {
        --angle: 0deg;
      }

      to {
        --angle: 360deg;
      }
    }

    /* webpack changes this to 75% for rtl if this is inline, so we create a property to avoid this */
    --transparent-percentage: 25%;

    position: absolute;
    top: -1px;
    left: -1px;
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    overflow: hidden;
    border-radius: var(--cds-border-radius-max);

    &::after {
      content: '';
      position: absolute;
      height: 100%;
      width: 100%;
      background: conic-gradient(
        from var(--angle),
        transparent var(--transparent-percentage),
        var(--coursera-ai-color-teal),
        var(--cds-color-emphasis-primary-background-default),
        var(--cds-color-emphasis-primary-background-default),
        var(--coursera-ai-color-teal),
        transparent
      );
      z-index: -2;
      border-radius: var(--cds-border-radius-max);
      animation: spin 3s linear forwards infinite;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }
  `,
};

const StyledCoachTriggerButton = React.forwardRef(
  (props: Props, ref: React.Ref<HTMLButtonElement>): React.ReactElement => {
    const {
      variant = 'secondary',
      size = 'small',
      className,
      selected,
      children,
      showAnimationOnMount = true,
      ...rest
    } = props;

    return (
      <Button
        ref={ref}
        variant={variant}
        textAlign="start"
        size={size}
        icon={<SparkleIcon size={size} />}
        iconPosition="before"
        css={styles.coachButton({ showBorder: !showAnimationOnMount })}
        className={cx(className, { selected }, variant === 'secondary' ? 'coach-secondary-button' : '')}
        {...rest}
      >
        {children}
        {showAnimationOnMount && <span className="animation-wrapper" css={styles.animated} />}
      </Button>
    );
  }
) as React.ComponentType<Props>;

export default StyledCoachTriggerButton;
